// A quick, fixed-length glide to a spot in a guide (a long guide must not take
// longer to cross than a short one): a search result's match, whose flash
// starts as the glide nears the word rather than off-screen before it, or a
// section picked from the table of contents.

const GLIDE_MS = 450;
/** Share of the glide's time after which `onNear` fires — ease-out has covered ~88% of the distance by then. */
const NEAR_AT = 0.5;

/**
 * Bring `target` to `block` (centre by default) in whichever ancestors scroll, gliding there, and call
 * `onNear` once as it closes in (at once when nothing needs to move, or the
 * user prefers reduced motion — then it is a plain jump). Returns a cancel
 * that stops the glide where it is.
 */
export function glideIntoView(target: HTMLElement, onNear: () => void, block: ScrollLogicalPosition = 'center'): () => void {
  // Let the browser work out the destination, then rewind and play it.
  const ancestors: HTMLElement[] = [];
  for (let element = target.parentElement; element; element = element.parentElement) ancestors.push(element);
  const before = ancestors.map((element) => element.scrollTop);
  target.scrollIntoView({ block });
  const moves = ancestors
    .map((element, i) => ({ element, from: before[i]!, to: element.scrollTop }))
    .filter((move) => move.to !== move.from);
  if (moves.length === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    onNear();
    return () => {};
  }

  for (const move of moves) move.element.scrollTop = move.from;
  let start: number | null = null;
  let near = false;
  let frame = 0;
  const step = (now: number) => {
    start ??= now;
    const t = Math.min(1, (now - start) / GLIDE_MS);
    const eased = 1 - (1 - t) ** 3;
    for (const move of moves) move.element.scrollTop = move.from + (move.to - move.from) * eased;
    if (!near && t >= NEAR_AT) {
      near = true;
      onNear();
    }
    if (t < 1) frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
}
